import type { ClientData, ClientSuccessMetric } from "../services/successDataService";
import { SUCCESS_UPDATE_INTERVAL_DAYS } from "../../../shared/success/successRecords";
import { toDate } from "../../../shared/utils/dateHelpers";

export interface SuccessOverviewStatistics {
  goalsAchievedLast30Days: number;
  targetsAchievedLast30Days: number;
  clientsWithAtLeastOneGoal: number;
  totalClientsWithData: number;
}

export interface SuccessOverviewModel {
  recentUpdates: ClientSuccessMetric[];
  overdueUpdates: ClientSuccessMetric[];
  clientSuccessMap: Map<string, ClientSuccessMetric[]>;
  statistics: SuccessOverviewStatistics;
}

const RECENT_UPDATES_PER_CLIENT = 2;
const STATISTICS_WINDOW_DAYS = 30;

// Only real updates count. Planned placeholders and assessment seeds stay in `clientSuccessMap`
// (so a client's page can still list them) but never feed Recent, Overdue or the statistics.
function isCounted(metric: ClientSuccessMetric): boolean {
  return metric.kind === "update";
}

function daysBefore(now: Date, days: number): Date {
  const date = new Date(now.getTime());
  date.setDate(date.getDate() - days);
  return date;
}

function firstSessionDateOf(client: ClientData): Date | null {
  if (client.first_session_date) {
    return toDate(client.first_session_date);
  }
  if (client.visit_dates && client.visit_dates.length > 0) {
    const validDates = client.visit_dates
      .map((d: any) => toDate(d))
      .filter((d: Date | null): d is Date => d !== null)
      .sort((a: Date, b: Date) => a.getTime() - b.getTime());
    if (validDates.length > 0) {
      return validDates[0];
    }
  }
  return null;
}

function awaitingFirstUpdateRow(client: ClientData, firstSessionDate: Date): ClientSuccessMetric {
  return {
    id: `placeholder-${client.client_id}`,
    client_id: client.client_id,
    client_name: client.client_name,
    coach_name: client.lead_coach_name,
    primary_location: client.primary_location,
    goal: "",
    goal_update: "",
    goal_achieved: false,
    target1: "",
    target2: "",
    target3: "",
    target1_update: "",
    target2_update: "",
    target3_update: "",
    target1_achieved: false,
    target2_achieved: false,
    target3_achieved: false,
    comments: "Awaiting first success update",
    created_by: "",
    created_by_uid: "",
    created_by_email: "",
    updated_at: firstSessionDate,
    created_at: firstSessionDate,
    kind: "planned",
    has_content: false,
    awaiting_first_update: true,
    first_session_date: firstSessionDate,
  };
}

export function buildSuccessOverview(
  metrics: ClientSuccessMetric[],
  clients: ClientData[],
  now: Date
): SuccessOverviewModel {
  // Every record of a client, whatever its kind, newest first.
  const clientSuccessMap = new Map<string, ClientSuccessMetric[]>();
  metrics.forEach((metric) => {
    const clientMetrics = clientSuccessMap.get(metric.client_id) || [];
    clientMetrics.push(metric);
    clientSuccessMap.set(metric.client_id, clientMetrics);
  });
  clientSuccessMap.forEach((clientMetrics) => {
    clientMetrics.sort((a, b) => b.updated_at.getTime() - a.updated_at.getTime());
  });

  // Counted records per client, still newest first.
  const countedByClient = new Map<string, ClientSuccessMetric[]>();
  clientSuccessMap.forEach((clientMetrics, clientId) => {
    const counted = clientMetrics.filter(isCounted);
    if (counted.length > 0) {
      countedByClient.set(clientId, counted);
    }
  });

  // Recent: a client's two newest counted records that say something.
  const recentUpdates: ClientSuccessMetric[] = [];
  countedByClient.forEach((counted) => {
    counted
      .filter((metric) => metric.has_content)
      .slice(0, RECENT_UPDATES_PER_CLIENT)
      .forEach((metric) => recentUpdates.push(metric));
  });
  recentUpdates.sort((a, b) => b.updated_at.getTime() - a.updated_at.getTime());

  // Overdue: active clients whose newest counted record is past the interval, or who have none
  // and started more than the interval ago.
  const overdueThreshold = daysBefore(now, SUCCESS_UPDATE_INTERVAL_DAYS);
  const overdueUpdates: ClientSuccessMetric[] = [];
  clients.forEach((client) => {
    if (client.active_programme !== true) return;

    const counted = countedByClient.get(client.client_id);
    if (counted) {
      if (counted[0].updated_at < overdueThreshold) {
        overdueUpdates.push(counted[0]);
      }
      return;
    }

    const firstSessionDate = firstSessionDateOf(client);
    if (firstSessionDate && firstSessionDate < overdueThreshold) {
      overdueUpdates.push(awaitingFirstUpdateRow(client, firstSessionDate));
    }
  });
  overdueUpdates.sort((a, b) => a.updated_at.getTime() - b.updated_at.getTime());

  // Statistics: counted records only.
  const windowStart = daysBefore(now, STATISTICS_WINDOW_DAYS);
  let goalsAchievedLast30Days = 0;
  let targetsAchievedLast30Days = 0;
  let clientsWithAtLeastOneGoal = 0;
  countedByClient.forEach((counted) => {
    if (counted.some((metric) => metric.goal_achieved)) {
      clientsWithAtLeastOneGoal++;
    }
    counted.forEach((metric) => {
      if (metric.updated_at >= windowStart) {
        if (metric.goal_achieved) goalsAchievedLast30Days++;
        if (metric.target1_achieved) targetsAchievedLast30Days++;
        if (metric.target2_achieved) targetsAchievedLast30Days++;
        if (metric.target3_achieved) targetsAchievedLast30Days++;
      }
    });
  });

  return {
    recentUpdates,
    overdueUpdates,
    clientSuccessMap,
    statistics: {
      goalsAchievedLast30Days,
      targetsAchievedLast30Days,
      clientsWithAtLeastOneGoal,
      totalClientsWithData: countedByClient.size,
    },
  };
}
